import { decodeParams, encodeParams, paramKeys } from '@fieldops/shared'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { z } from 'zod'

/**
 * List state held in the URL rather than in component state.
 *
 * This is what makes every view shareable: the filters someone is looking at
 * are in the address bar, so pasting the link into a message opens exactly
 * that view for the next person. It also means the back button works through
 * filter changes, and a refresh does not throw the view away.
 *
 * Defaults are omitted from the URL by the shared codec, so a link carries
 * only what was actually chosen.
 *
 * A schema owns its own keys and nothing else. Anything else in the query is
 * carried through untouched, because the open job popup rides on every screen
 * as `?jobId=` and picking a filter must not close it.
 */
export function useUrlState<TSchema extends z.ZodType>(
  schema: TSchema,
): [z.infer<TSchema>, (update: Partial<z.infer<TSchema>>, options?: { replace?: boolean }) => void] {
  const [searchParams, setSearchParams] = useSearchParams()

  const state = useMemo(
    () => decodeParams(schema, searchParams),
    // The string is the real dependency; the object identity changes on every
    // render even when nothing about the query has.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schema, searchParams.toString()],
  )

  const update = useCallback(
    (patch: Partial<z.infer<TSchema>>, options?: { replace?: boolean }) => {
      const next = { ...(state as object), ...patch } as z.infer<TSchema>
      const owned = new Set(paramKeys(schema))

      // Changing a filter goes back to the first page. Staying on page 4 of a
      // result set that now has two pages shows an empty screen and looks
      // like the filter matched nothing. Only for a schema that has pages: the
      // board has none, and a `page=1` it did not own was carried along and
      // added again on every click.
      const changedFilter = Object.keys(patch).some((key) => key !== 'page')
      if (owned.has('page') && changedFilter && !('page' in patch)) {
        ;(next as { page?: number }).page = 1
      }

      const encoded = encodeParams(schema, next)
      for (const [key, value] of searchParams.entries()) {
        if (!owned.has(key)) encoded.append(key, value)
      }

      setSearchParams(encoded, {
        // Typing in a search box should not put twenty entries in the history
        // stack; picking a filter should be undoable with the back button.
        replace: options?.replace ?? false,
      })
    },
    [schema, searchParams, state, setSearchParams],
  )

  return [state, update]
}
